import type { AssetKind } from '@/api/asset'

/** 各类型的大小上限与允许扩展名，与后端保持一致（技术规格 §3.4）。前端预检只是省一次往返，后端仍是权威。 */
export const ASSET_LIMITS: Record<AssetKind, { maxBytes: number; exts: string[]; label: string }> = {
  bg: { maxBytes: 2 * 1024 * 1024, exts: ['jpg', 'jpeg', 'png', 'webp'], label: '背景图' },
  portrait: { maxBytes: 800 * 1024, exts: ['jpg', 'jpeg', 'png', 'webp'], label: '人物立绘' },
  avatar: { maxBytes: 200 * 1024, exts: ['jpg', 'jpeg', 'png', 'webp'], label: '角色头像' },
  item: { maxBytes: 200 * 1024, exts: ['jpg', 'jpeg', 'png', 'webp'], label: '通用物品' },
  // mp4（2026-10-09 用户要求）：视频容器只取音轨播放，后端内容嗅探按 ftyp 识别为 audio/mp4，
  // OSS 对象名规范化为 .m4a；时长读取走下面的 readAudioDuration（浏览器 <audio> 能解码其音轨）
  bgm: { maxBytes: 10 * 1024 * 1024, exts: ['mp3', 'm4a', 'mp4'], label: '背景音乐' },
  sfx: { maxBytes: 2 * 1024 * 1024, exts: ['mp3', 'm4a', 'mp4'], label: '音效' },
}

export const AUDIO_KINDS: AssetKind[] = ['bgm', 'sfx']

export function isAudioKind(kind: AssetKind): boolean {
  return AUDIO_KINDS.indexOf(kind) >= 0
}

function extOf(filename: string): string {
  const i = filename.lastIndexOf('.')
  return i < 0 ? '' : filename.slice(i + 1).toLowerCase()
}

function humanSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    const mb = bytes / 1024 / 1024
    return (Number.isInteger(mb) ? mb : mb.toFixed(1)) + 'MB'
  }
  return Math.round(bytes / 1024) + 'KB'
}

/**
 * 上传前预检。通过返回 null，否则返回可直接弹给作者的中文文案。
 * 只查扩展名与大小；内容嗅探由后端做。
 */
export function validateAssetFile(file: File, kind: AssetKind): string | null {
  const limit = ASSET_LIMITS[kind]
  if (!limit) return '未知的素材类型'
  const ext = extOf(file.name)
  if (limit.exts.indexOf(ext) < 0) {
    return `${limit.label}只支持 ${limit.exts.join(' / ')} 格式，当前是 ${ext || '无扩展名'}`
  }
  if (file.size > limit.maxBytes) {
    return `${limit.label}不能超过 ${humanSize(limit.maxBytes)}，当前 ${humanSize(file.size)}`
  }
  if (file.size === 0) return '文件是空的'
  return null
}

/**
 * 读音频时长（秒）。后端 duration_sec 必填，只能由前端提供（W6）。
 * 读不到（浏览器不支持该编码）时 reject，调用方要提示作者换文件，
 * **不许瞎填一个数糊弄过去**——那会让 App 端的音轨行为出错。
 */
export function readAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const audio = new Audio()
    const cleanup = () => URL.revokeObjectURL(url)
    audio.addEventListener('loadedmetadata', () => {
      const d = audio.duration
      cleanup()
      if (!isFinite(d) || d <= 0) {
        reject(new Error('读不到音频时长，请换一个文件'))
        return
      }
      resolve(Math.round(d * 100) / 100) // 保留两位小数，与截图的显示口径一致
    })
    audio.addEventListener('error', () => {
      cleanup()
      reject(new Error('读不到音频时长，请换一个文件'))
    })
    audio.src = url
  })
}

/** 读图片宽高，随上传一起提交（后端字段可选，用于以后按尺寸筛选） */
export function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('读不到图片尺寸'))
    }
    img.src = url
  })
}
